import React, { useEffect, useState } from 'react';

import { Button } from 'semantic-ui-react';

import _camelCase from 'lodash/camelCase';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import { createUser } from 'actions/user';
import { InlineFieldContainer, InputField } from 'components/common';
import FormContainer from 'components/common/FormContainer';
import PhoneField from 'components/modules/fields/components/PhoneField';
import { DismissibleMessage } from 'components/modules/utils/components';
import API from 'libs/api';
import { getErrorMessage } from 'libs/errors';
import { multiCaseGet } from 'libs/format';
import { selectUser } from 'selectors/user';

import {
  getFormValidationMessage,
  getInitialFormData,
  getUpdatePayload,
} from './AccountInfoForm.utils';

import './AccountInfoForm.scss';

function AccountInfoForm() {
  const [formData, setFormData] = useState({});
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  function getValue(fieldName, defaultValue = '') {
    return multiCaseGet(formData, fieldName, defaultValue);
  }

  const firstName = getValue('firstName');
  const lastName = getValue('lastName');
  const phoneNumber = getValue('phoneNumber');
  const email = getValue('email');

  useEffect(() => {
    if (_isEmpty(user)) {
      return;
    }
    if (_isEmpty(formData)) {
      setFormData(getInitialFormData(user));
    }
  }, [user]);

  function onChange(e, { name, value }) {
    setTouched(true);
    const processedValue = name === 'email' ? value.toLowerCase() : value;

    const camelCaseName = _camelCase(name);
    setFormData((prev) => ({
      ...prev,
      [camelCaseName]: processedValue,
    }));
  }

  async function onSubmit() {
    const validationMessage = getFormValidationMessage(formData);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    const userId = multiCaseGet(user, 'id');

    setLoading(true);
    setErrorMessage('');

    try {
      const updatedUser = await API.updateUser(userId, getUpdatePayload(user, formData));
      dispatch(createUser(updatedUser));

      setFormData(getInitialFormData(updatedUser));
    } catch (e) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormContainer className="account-info-form" loadedKeyPath={['user', 'core']}>
      <InlineFieldContainer title={<h2>Personal</h2>} enableDelete={false}>
        <div>
          <InputField
            title="First Name"
            name="first_name"
            value={firstName}
            placeholder="First Name"
            onChange={onChange}
            disabled={loading}
            required
          />
          <InputField
            title="Last Name"
            name="last_name"
            value={lastName}
            placeholder="Last Name"
            onChange={onChange}
            disabled={loading}
            required
          />
        </div>
      </InlineFieldContainer>
      <InlineFieldContainer title={<h2>Contact</h2>} enableDelete={false}>
        <div>
          <PhoneField
            title="Phone Number"
            name="phone_number"
            value={phoneNumber}
            placeholder="Phone Number"
            onChange={onChange}
            disabled={loading}
            required
          />
          <InputField
            title="Email"
            name="email"
            type="email"
            value={email}
            placeholder="Email"
            onChange={onChange}
            disabled={loading}
            required
          />
        </div>
        <div className="actions">
          {errorMessage && (
            <DismissibleMessage error initialVisible onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </DismissibleMessage>
          )}
          <Button
            className="action"
            type="button"
            onClick={onSubmit}
            loading={loading}
            disabled={
              _isEmpty(formData) || loading || !touched || getFormValidationMessage(formData)
            }
          >
            Save Changes
          </Button>
        </div>
      </InlineFieldContainer>
    </FormContainer>
  );
}

export default AccountInfoForm;
